/**
 * @module @shared/config/env
 * Liest die Vite-Umgebungsvariablen der Admin-App mit robusten Defaults aus.
 * Alle Werte dürfen Platzhalter sein — die App muss trotzdem rendern (kein Crash
 * bei fehlendem zentralen `gmolike-id`-Projekt). Die Datenschicht zeigt dann
 * sauber Loading/Empty/Error statt zu werfen.
 */
import type { GlobalLoginConfig } from '@repo/global-login-sdk';

/** Sicheres Auslesen einer Vite-Env-Variable mit Fallback. */
function readEnv(value: string | undefined, fallback: string): string {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : fallback;
}

/**
 * Zentral aufbereitete Umgebung der Admin-App. Werte stammen aus `import.meta.env`
 * (zur Build-Zeit gebacken) und fallen auf entwicklungssichere Defaults zurück.
 */
export const env = {
  /** URL des zentralen Auth-Projekts (`gmolike-id`). Platzhalter, falls noch nicht angelegt. */
  supabaseUrl: readEnv(import.meta.env.VITE_SUPABASE_URL, 'https://placeholder.supabase.co'),
  /** Publishable Key (`sb_publishable_…`) — client-safe. */
  publishableKey: readEnv(
    import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    'sb_publishable_placeholder',
  ),
  /** Slug dieser App in `identity.apps`. Die Admin-App selbst ist `admin`. */
  appSlug: readEnv(import.meta.env.VITE_APP_SLUG, 'admin'),
  /** Basis-URL der zentralen Login-App. */
  loginUrl: readEnv(import.meta.env.VITE_LOGIN_URL, 'https://auth.gmolike.me'),
  /**
   * Basis-URL des service-role-gestützten Identity-Admin-Workers
   * (`identity-admin-api`) für Username-/Email-Auflösung. Leer (Default) =>
   * Worker-Features inaktiv; die Datenschicht liefert dann neutrale Leerergebnisse.
   */
  identityApiUrl: readEnv(import.meta.env.VITE_IDENTITY_API_URL, ''),
  /** Cookie-Domain für subdomain-weites SSO (lokal ggf. leer). */
  cookieDomain: readEnv(import.meta.env.VITE_COOKIE_DOMAIN, '.gmolike.me'),
  /**
   * DEV-ONLY: Auth-Gate überspringen + Fixture-Daten laden.
   * Aktivieren via `VITE_DEV_BYPASS_AUTH=true` in `.env.local`.
   * In Produktion immer `false` (Vite ersetzt `import.meta.env.DEV` durch `false`).
   */
  devBypassAuth: import.meta.env.DEV && import.meta.env.VITE_DEV_BYPASS_AUTH === 'true',
} as const;

/** Rolle, die für den Zugang zur Admin-App zwingend erforderlich ist. */
export const ADMIN_ROLE = 'admin';

/**
 * Konfigurationsvertrag für das `@repo/global-login-sdk`. Wird vom Supabase-Client
 * und vom `GlobalLoginProvider` genutzt.
 */
export const globalLoginConfig: GlobalLoginConfig = {
  supabaseUrl: env.supabaseUrl,
  publishableKey: env.publishableKey,
  appSlug: env.appSlug,
  loginUrl: env.loginUrl,
  cookieDomain: env.cookieDomain,
};
